/**
 * System Logs Vuex Store Module
 * 
 * Manages state for the system logs viewer (LAB_SUPER_ADMIN only)
 */
import axios from "@/config/axios";

const initialState = () => {
  return {
    logs: [],
    stats: null,
    errors: [],
    thirdParty: [],
    performance: [],
    availableDates: [],
    counts: null,
    loading: false,
    selectedLog: null,
    filters: {
      type: '',
      level: '',
      search: '',
      startDate: '',
      endDate: '',
      serviceName: '',
      path: '',
      statusCode: '',
      minDuration: '',
      accessionId: '',
      orderId: '',
      timeRangeMinutes: 60
    },
    pagination: {
      page: 1,
      limit: 100,
      total: 0
    }
  };
};

const state = initialState();

const getters = {
  getLogs: state => state.logs,
  getStats: state => state.stats,
  getErrors: state => state.errors,
  getThirdParty: state => state.thirdParty,
  getPerformance: state => state.performance,
  getAvailableDates: state => state.availableDates,
  getCounts: state => state.counts,
  getLoading: state => state.loading,
  getSelectedLog: state => state.selectedLog,
  getFilters: state => state.filters,
  getPagination: state => state.pagination
};

const mutations = {
  setLogs(state, logs) {
    state.logs = logs;
  },
  setStats(state, stats) {
    state.stats = stats;
  },
  setErrors(state, errors) {
    state.errors = errors;
  },
  setThirdParty(state, thirdParty) {
    state.thirdParty = thirdParty;
  },
  setPerformance(state, performance) {
    state.performance = performance;
  },
  setAvailableDates(state, dates) {
    state.availableDates = dates;
  },
  setCounts(state, counts) {
    state.counts = counts;
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setSelectedLog(state, log) {
    state.selectedLog = log;
  },
  setFilters(state, filters) {
    state.filters = { ...state.filters, ...filters };
  },
  setPagination(state, pagination) {
    state.pagination = { ...state.pagination, ...pagination };
  },
  reset: state => {
    const initial = initialState();
    Object.keys(initial).forEach(key => {
      state[key] = initial[key];
    });
  }
};

const actions = {
  // Fetch recent logs with filters
  async fetchLogs({ commit, dispatch, state }) {
    try {
      commit("setLoading", true);
      await dispatch("errors/clear", [], { root: true });

      const params = {
        limit: state.pagination.limit,
        ...state.filters
      };

      // Remove empty params
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await axios.get("/system-logs", { params });
      
      commit("setLogs", response.data.data || []);
      commit("setPagination", { total: response.data.total || 0 });
    } catch (error) {
      if (error.response) {
        await dispatch("errors/errorResponse", error.response, { root: true });
      }
    } finally {
      commit("setLoading", false);
    }
  },

  // Fetch logs from a date range (file-based for historical data)
  async fetchLogsByDateRange({ commit, dispatch }, options) {
    try {
      commit("setLoading", true);
      await dispatch("errors/clear", [], { root: true });

      const params = { ...options };
      
      // Remove empty params
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await axios.get("/system-logs/date-range", { params });
      
      commit("setLogs", response.data.data || []);
      commit("setPagination", { total: response.data.total || 0 });
      
      return response.data;
    } catch (error) {
      if (error.response) {
        await dispatch("errors/errorResponse", error.response, { root: true });
      }
      return null;
    } finally {
      commit("setLoading", false);
    }
  },

  // Fetch log statistics
  async fetchStats({ commit, dispatch, state }) {
    try {
      commit("setLoading", true);
      await dispatch("errors/clear", [], { root: true });

      const response = await axios.get("/system-logs/stats", {
        params: { timeRangeMinutes: state.filters.timeRangeMinutes }
      });
      
      commit("setStats", response.data.data);
    } catch (error) {
      if (error.response) {
        await dispatch("errors/errorResponse", error.response, { root: true });
      }
    } finally {
      commit("setLoading", false);
    }
  },

  // Fetch error summary
  async fetchErrors({ commit, dispatch, state }) {
    try {
      commit("setLoading", true);
      await dispatch("errors/clear", [], { root: true });

      const response = await axios.get("/system-logs/errors", {
        params: { timeRangeMinutes: state.filters.timeRangeMinutes, limit: 100 }
      });
      
      commit("setErrors", response.data.data || []);
    } catch (error) {
      if (error.response) {
        await dispatch("errors/errorResponse", error.response, { root: true });
      }
    } finally {
      commit("setLoading", false);
    }
  },

  // Fetch third-party calls summary
  async fetchThirdParty({ commit, dispatch, state }) {
    try {
      commit("setLoading", true);
      await dispatch("errors/clear", [], { root: true });

      const response = await axios.get("/system-logs/third-party", {
        params: { timeRangeMinutes: state.filters.timeRangeMinutes, limit: 100 }
      });
      
      commit("setThirdParty", response.data.data || []);
    } catch (error) {
      if (error.response) {
        await dispatch("errors/errorResponse", error.response, { root: true });
      }
    } finally {
      commit("setLoading", false);
    }
  },

  // Fetch performance report
  async fetchPerformance({ commit, dispatch, state }) {
    try {
      commit("setLoading", true);
      await dispatch("errors/clear", [], { root: true });

      const response = await axios.get("/system-logs/performance", {
        params: { 
          timeRangeMinutes: state.filters.timeRangeMinutes,
          threshold: 2000,
          limit: 50
        }
      });
      
      commit("setPerformance", response.data.data || []);
    } catch (error) {
      if (error.response) {
        await dispatch("errors/errorResponse", error.response, { root: true });
      }
    } finally {
      commit("setLoading", false);
    }
  },

  // Fetch available log dates
  async fetchAvailableDates({ commit, dispatch }) {
    try {
      await dispatch("errors/clear", [], { root: true });

      const response = await axios.get("/system-logs/dates");
      
      commit("setAvailableDates", response.data.data || []);
    } catch (error) {
      if (error.response) {
        await dispatch("errors/errorResponse", error.response, { root: true });
      }
    }
  },

  // Fetch log counts
  async fetchCounts({ commit, dispatch, state }) {
    try {
      await dispatch("errors/clear", [], { root: true });

      const response = await axios.get("/system-logs/counts", {
        params: { timeRangeMinutes: state.filters.timeRangeMinutes }
      });
      
      commit("setCounts", response.data.data);
    } catch (error) {
      if (error.response) {
        await dispatch("errors/errorResponse", error.response, { root: true });
      }
    }
  },

  // Fetch logs by date
  async fetchLogsByDate({ commit, dispatch, state }, date) {
    try {
      commit("setLoading", true);
      await dispatch("errors/clear", [], { root: true });

      const params = {
        limit: state.pagination.limit,
        search: state.filters.search,
        level: state.filters.level,
        type: state.filters.type
      };

      // Remove empty params
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await axios.get(`/system-logs/date/${date}`, { params });
      
      commit("setLogs", response.data.data || []);
      commit("setPagination", { total: response.data.total || 0 });
    } catch (error) {
      if (error.response) {
        await dispatch("errors/errorResponse", error.response, { root: true });
      }
    } finally {
      commit("setLoading", false);
    }
  },

  // Search logs by accession
  async searchByAccession({ commit, dispatch }, accessionId) {
    try {
      commit("setLoading", true);
      await dispatch("errors/clear", [], { root: true });

      const response = await axios.get(`/system-logs/accession/${accessionId}`);
      
      commit("setLogs", response.data.data || []);
      commit("setPagination", { total: response.data.total || 0 });
    } catch (error) {
      if (error.response) {
        await dispatch("errors/errorResponse", error.response, { root: true });
      }
    } finally {
      commit("setLoading", false);
    }
  },

  // Update filters
  updateFilters({ commit }, filters) {
    commit("setFilters", filters);
  },

  // Select a log for detail view
  selectLog({ commit }, log) {
    commit("setSelectedLog", log);
  },

  // Clear selected log
  clearSelectedLog({ commit }) {
    commit("setSelectedLog", null);
  },

  // Reset state
  resetState({ commit }) {
    commit("reset");
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
